import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { 
  Package,
  Plus,
  Pencil,
  Trash,
  Check,
  MagnifyingGlass,
  UploadSimple,
  ArrowUp,
  ArrowDown,
  MapPin,
  Warning,
  ArrowsClockwise
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [allStorageAreas, setAllStorageAreas] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [migrationDialogOpen, setMigrationDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [duplicates, setDuplicates] = useState({ duplicates: [], conflicts: [] });
  const [conflictResolution, setConflictResolution] = useState(null);
  
  // Form state - now includes location_mappings
  const [formData, setFormData] = useState({
    name: "",
    unit: "",
    category: "",
    price: "",
    low_stock_threshold: "10",
    location_mappings: [], // [{location_id, storage_area_id, order}]
    oz_conversion: "",
    pans_to_oz: "",  // How many oz in 1 pan (for waste log)
    allow_in_waste_log: false,
  });
  const [uploadFile, setUploadFile] = useState(null);

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchProducts(selectedLocation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  const fetchInitialData = async () => {
    try {
      const [locRes, areasRes] = await Promise.all([
        axios.get(`${API}/locations`, { withCredentials: true }),
        axios.get(`${API}/storage-areas`, { withCredentials: true })
      ]);
      setLocations(locRes.data);
      setAllStorageAreas(areasRes.data);
      
      if (locRes.data.length > 0) {
        setSelectedLocation(locRes.data[0].location_id);
      }
      
      // Check migration status
      checkMigrationStatus();
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async (locationId) => {
    try {
      const response = await axios.get(`${API}/products?location_id=${locationId}`, { withCredentials: true });
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to load products");
    }
  };

  const fetchAllProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/all`, { withCredentials: true });
      setAllProducts(response.data);
    } catch (error) {
      console.error("Failed to load all products");
    }
  };

  const checkMigrationStatus = async () => {
    try {
      const response = await axios.get(`${API}/products/migration/status`, { withCredentials: true });
      setMigrationStatus(response.data);
      
      if (!response.data.migration_complete) {
        // Fetch duplicates if migration not complete
        const dupResponse = await axios.get(`${API}/products/migration/detect-duplicates`, { withCredentials: true });
        setDuplicates(dupResponse.data);
      }
    } catch (error) {
      console.error("Failed to check migration status");
    }
  };

  const handleAutoMerge = async () => {
    try {
      const response = await axios.post(`${API}/products/migration/auto-merge`, {}, { withCredentials: true });
      toast.success(response.data.message);
      checkMigrationStatus();
      if (selectedLocation) {
        fetchProducts(selectedLocation);
      }
    } catch (error) {
      toast.error("Failed to auto-merge products");
    }
  };

  const handleResolveConflict = async (targetProductId, sourceProductIds) => {
    try {
      await axios.post(`${API}/products/migration/resolve-conflict`, {
        target_product_id: targetProductId,
        source_product_ids: sourceProductIds
      }, { withCredentials: true });
      toast.success("Products merged successfully");
      setConflictResolution(null);
      checkMigrationStatus();
      if (selectedLocation) {
        fetchProducts(selectedLocation);
      }
    } catch (error) {
      toast.error("Failed to resolve conflict");
    }
  };

  const handleAssignLocation = async (productId, locationId, storageAreaId) => {
    try {
      await axios.post(`${API}/products/${productId}/assign-location?location_id=${locationId}&storage_area_id=${storageAreaId}`, {}, { withCredentials: true });
      toast.success("Location assigned successfully");
      checkMigrationStatus();
      if (selectedLocation) {
        fetchProducts(selectedLocation);
      }
    } catch (error) {
      toast.error("Failed to assign location");
    }
  };

  const getStorageAreasForLocation = (locationId) => {
    return allStorageAreas.filter(a => a.location_id === locationId);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error("Please fill in product name");
      return;
    }

    if (formData.location_mappings.length === 0) {
      toast.error("Please add at least one location");
      return;
    }

    // Validate each mapping has a storage area
    for (const mapping of formData.location_mappings) {
      if (!mapping.storage_area_id) {
        const loc = locations.find(l => l.location_id === mapping.location_id);
        toast.error(`Please select a storage area for ${loc?.name || 'location'}`);
        return;
      }
    }

    // Check oz_conversion for waste log
    const unitUpper = (formData.unit || "").toUpperCase();
    const isLbUnit = unitUpper.includes("LB");
    const needsOzConversion = !isLbUnit && formData.allow_in_waste_log;
    
    if (needsOzConversion && !formData.oz_conversion) {
      toast.error("Please enter oz conversion for waste log tracking");
      return;
    }

    try {
      let ozConversion = formData.oz_conversion ? parseFloat(formData.oz_conversion) : null;
      if (isLbUnit) {
        ozConversion = 16;
      }

      const payload = {
        name: formData.name,
        unit: formData.unit,
        category: formData.category,
        price: parseFloat(formData.price) || 0,
        low_stock_threshold: parseFloat(formData.low_stock_threshold) || 10,
        location_mappings: formData.location_mappings,
        oz_conversion: ozConversion,
        pans_to_oz: formData.pans_to_oz ? parseFloat(formData.pans_to_oz) : null,
        allow_in_waste_log: formData.allow_in_waste_log
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.product_id}`, payload, { withCredentials: true });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/products`, payload, { withCredentials: true });
        toast.success("Product created");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchProducts(selectedLocation);
    } catch (error) {
      toast.error("Failed to save product");
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      unit: product.unit || "",
      category: product.category || "",
      price: product.price?.toString() || "",
      low_stock_threshold: product.low_stock_threshold?.toString() || "10",
      location_mappings: product.location_mappings || [],
      oz_conversion: product.oz_conversion?.toString() || "",
      pans_to_oz: product.pans_to_oz?.toString() || "",
      allow_in_waste_log: product.allow_in_waste_log || false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Delete this product?")) return;
    
    try {
      await axios.delete(`${API}/products/${productId}`, { withCredentials: true });
      toast.success("Product deleted");
      fetchProducts(selectedLocation);
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const handleMoveOrder = async (product, direction) => {
    const currentMapping = product.location_mappings?.find(m => m.location_id === selectedLocation);
    if (!currentMapping) return;
    
    const newOrder = direction === "up" ? currentMapping.order - 1 : currentMapping.order + 1;
    try {
      await axios.put(
        `${API}/products/${product.product_id}/location-order?location_id=${selectedLocation}&order=${newOrder}`,
        {},
        { withCredentials: true }
      );
      fetchProducts(selectedLocation);
    } catch (error) {
      toast.error("Failed to update order");
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: "",
      unit: "",
      category: "",
      price: "",
      low_stock_threshold: "10",
      location_mappings: [],
      oz_conversion: "",
      pans_to_oz: "",
      allow_in_waste_log: false,
    });
  };

  const addLocationMapping = (locationId, storageAreaId = null) => {
    const areas = getStorageAreasForLocation(locationId);
    const areaId = storageAreaId || areas[0]?.area_id || "";
    
    // Check if this exact location + storage area combo already exists
    if (formData.location_mappings.some(m => m.location_id === locationId && m.storage_area_id === areaId)) {
      toast.error("This location/area combination already exists");
      return;
    }
    
    setFormData(p => ({
      ...p,
      location_mappings: [
        ...p.location_mappings,
        {
          location_id: locationId,
          storage_area_id: areaId,
          order: p.location_mappings.filter(m => m.location_id === locationId).length
        }
      ]
    }));
  };

  const removeLocationMapping = (locationId, storageAreaId) => {
    setFormData(p => ({
      ...p,
      location_mappings: p.location_mappings.filter(m => 
        !(m.location_id === locationId && m.storage_area_id === storageAreaId)
      )
    }));
  };

  const updateLocationMapping = (locationId, storageAreaId, field, value) => {
    setFormData(p => ({
      ...p,
      location_mappings: p.location_mappings.map(m => 
        (m.location_id === locationId && m.storage_area_id === storageAreaId) 
          ? { ...m, [field]: value } 
          : m
      )
    }));
  };

  const getLocationName = (locationId) => {
    return locations.find(l => l.location_id === locationId)?.name || "Unknown";
  };

  const getAreaName = (areaId) => {
    return allStorageAreas.find(a => a.area_id === areaId)?.name || "Unknown";
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="products-page" className="space-y-6">
      {/* Migration Banner */}
      {migrationStatus && !migrationStatus.migration_complete && (
        <Card className="border-orange-300 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Warning size={24} className="text-orange-600" />
                <div>
                  <p className="font-medium text-orange-800">Product Migration Required</p>
                  <p className="text-sm text-orange-700">
                    {migrationStatus.not_migrated} products need to be consolidated. 
                    {duplicates.conflicts?.length > 0 && ` ${duplicates.conflicts.length} need manual resolution.`}
                    {migrationStatus.orphaned_products?.length > 0 && (
                      <span className="font-medium"> ({migrationStatus.orphaned_products.length} need location assignment)</span>
                    )}
                  </p>
                </div>
              </div>
              <Button onClick={() => setMigrationDialogOpen(true)} variant="outline" className="border-orange-400 text-orange-700">
                <ArrowsClockwise size={16} className="mr-2" />
                Run Migration
              </Button>
            </div>
            
            {/* Orphaned Products Quick Fix */}
            {migrationStatus.orphaned_products?.length > 0 && (
              <div className="mt-4 border-t border-orange-200 pt-4">
                <p className="font-heading tracking-wide text-xs text-orange-800 mb-2">
                  PRODUCTS NEEDING LOCATION ASSIGNMENT
                </p>
                <div className="space-y-2">
                  {migrationStatus.orphaned_products.map(product => (
                    <OrphanedProductRow 
                      key={product.product_id}
                      product={product}
                      locations={locations}
                      allStorageAreas={allStorageAreas}
                      onAssign={handleAssignLocation}
                    />
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl tracking-wide font-semibold">
            PRODUCTS
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage products across all locations
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-primary" data-testid="add-product-btn">
            <Plus size={18} className="mr-2" />
            Add Product
          </Button>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="w-full sm:w-48">
          <Select value={selectedLocation || ""} onValueChange={setSelectedLocation}>
            <SelectTrigger data-testid="products-location-select">
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent>
              {locations.map(loc => (
                <SelectItem key={loc.location_id} value={loc.location_id}>
                  {loc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input 
            data-testid="search-products-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search products..."
            className="pl-10"
          />
        </div>
      </div>

      {/* Products Table */}
      <Card className="border rounded-xl">
        <CardContent className="p-0">
          {filteredProducts.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-heading tracking-wide w-16">ORDER</TableHead>
                    <TableHead className="font-heading tracking-wide">PRODUCT</TableHead>
                    <TableHead className="font-heading tracking-wide">AREA</TableHead>
                    <TableHead className="font-heading tracking-wide">UNIT</TableHead>
                    <TableHead className="font-heading tracking-wide">CATEGORY</TableHead>
                    <TableHead className="font-heading tracking-wide text-right">PRICE</TableHead>
                    <TableHead className="font-heading tracking-wide">LOCATIONS</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.map((product, idx) => {
                    const currentMapping = product.location_mappings?.find(m => m.location_id === selectedLocation);
                    return (
                      <TableRow key={product.product_id} data-testid={`product-row-${product.product_id}`}>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6"
                              onClick={() => handleMoveOrder(product, "up")}
                              disabled={idx === 0}
                            >
                              <ArrowUp size={14} />
                            </Button>
                            <span className="text-center text-sm">{currentMapping?.order || product.order || 0}</span>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6"
                              onClick={() => handleMoveOrder(product, "down")}
                              disabled={idx === filteredProducts.length - 1}
                            >
                              <ArrowDown size={14} />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{product.name}</span>
                            {product.allow_in_waste_log && (
                              <Badge variant="outline" className="text-xs border-red-300 text-red-600">Waste</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getAreaName(currentMapping?.storage_area_id || product.storage_area_id)}</TableCell>
                        <TableCell className="text-sm">{product.unit}</TableCell>
                        <TableCell>{product.category || "-"}</TableCell>
                        <TableCell className="text-right font-medium">${product.price?.toFixed(2)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {(product.location_mappings || []).map(m => (
                              <Badge key={m.location_id} variant="secondary" className="text-xs">
                                {getLocationName(m.location_id).substring(0, 3)}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(product)}>
                              <Pencil size={16} />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(product.product_id)}>
                              <Trash size={16} className="text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              No products found. Add your first product to get started.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Product Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-wide">
              {editingProduct ? "EDIT PRODUCT" : "ADD PRODUCT"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Product Name</Label>
              <Input 
                data-testid="product-name-input"
                value={formData.name}
                onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Chicken Breast"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unit Definition</Label>
                <Input 
                  data-testid="product-unit-input"
                  value={formData.unit}
                  onChange={(e) => setFormData(p => ({ ...p, unit: e.target.value }))}
                  placeholder="e.g., 1 CS = 20 LB"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Examples: <span className="font-mono">1 CS = 12 EA</span>, <span className="font-mono">1 CS = 20 LB</span>
                </p>
              </div>
              <div>
                <Label>Category</Label>
                <Input 
                  data-testid="product-category-input"
                  value={formData.category}
                  onChange={(e) => setFormData(p => ({ ...p, category: e.target.value }))}
                  placeholder="e.g., Proteins"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price ($) - Global</Label>
                <Input 
                  data-testid="product-price-input"
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData(p => ({ ...p, price: e.target.value }))}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <Label>Low Stock Threshold</Label>
                <Input 
                  data-testid="product-threshold-input"
                  type="number"
                  value={formData.low_stock_threshold}
                  onChange={(e) => setFormData(p => ({ ...p, low_stock_threshold: e.target.value }))}
                  placeholder="10"
                  min="0"
                />
              </div>
            </div>

            {/* Location Mappings - Now supports multiple storage areas per location */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">Locations & Storage Areas</Label>
                  <p className="text-xs text-muted-foreground">Add multiple storage areas per location for separate counting</p>
                </div>
                <Select onValueChange={(val) => addLocationMapping(val)}>
                  <SelectTrigger className="w-[180px]">
                    <Plus size={14} className="mr-2" />
                    <SelectValue placeholder="Add Location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc.location_id} value={loc.location_id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {formData.location_mappings.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No locations added. Add locations where this product will be tracked.
                </p>
              ) : (
                <div className="space-y-2">
                  {/* Group mappings by location */}
                  {[...new Set(formData.location_mappings.map(m => m.location_id))].map(locId => {
                    const locMappings = formData.location_mappings.filter(m => m.location_id === locId);
                    const areas = getStorageAreasForLocation(locId);
                    return (
                      <div key={locId} className="border rounded-lg p-3 bg-muted/20">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <MapPin size={18} className="text-primary" />
                            <span className="font-medium">{getLocationName(locId)}</span>
                            <Badge variant="secondary" className="text-xs">{locMappings.length} area{locMappings.length > 1 ? 's' : ''}</Badge>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => addLocationMapping(locId)}
                            className="h-7 text-xs"
                          >
                            <Plus size={12} className="mr-1" />
                            Add Area
                          </Button>
                        </div>
                        <div className="space-y-2 pl-6">
                          {locMappings.map((mapping, idx) => (
                            <div key={`${mapping.location_id}-${mapping.storage_area_id}-${idx}`} className="flex items-center gap-2">
                              <Select 
                                value={mapping.storage_area_id}
                                onValueChange={(val) => {
                                  // Update the specific mapping
                                  const newMappings = [...formData.location_mappings];
                                  const mappingIndex = newMappings.findIndex(m => 
                                    m.location_id === mapping.location_id && m.storage_area_id === mapping.storage_area_id
                                  );
                                  if (mappingIndex !== -1) {
                                    newMappings[mappingIndex] = { ...mapping, storage_area_id: val };
                                    setFormData(p => ({ ...p, location_mappings: newMappings }));
                                  }
                                }}
                              >
                                <SelectTrigger className="h-8 flex-1">
                                  <SelectValue placeholder="Select storage area" />
                                </SelectTrigger>
                                <SelectContent>
                                  {areas.map(area => (
                                    <SelectItem key={area.area_id} value={area.area_id}>
                                      {area.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => removeLocationMapping(mapping.location_id, mapping.storage_area_id)}
                              >
                                <Trash size={14} className="text-destructive" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Allow in Waste Log */}
            <div className="flex items-center gap-3 pt-2">
              <input
                type="checkbox"
                id="product-allow-waste-log"
                checked={formData.allow_in_waste_log}
                onChange={(e) => setFormData(p => ({ ...p, allow_in_waste_log: e.target.checked }))}
                className="h-4 w-4 rounded border-gray-300"
                data-testid="product-allow-waste-log-checkbox"
              />
              <Label htmlFor="product-allow-waste-log" className="cursor-pointer font-medium">
                Allow in Waste Log
              </Label>
            </div>
            
            {/* Oz Conversion */}
            {formData.allow_in_waste_log && (
              <div className="pt-2">
                <Label>
                  Oz Conversion 
                  {(formData.unit || "").toUpperCase().includes("LB") ? (
                    <span className="text-xs text-green-600 ml-2">(Auto: 1 LB = 16 oz)</span>
                  ) : (
                    <span className="text-xs text-orange-600 ml-2">* Required for EA/CS</span>
                  )}
                </Label>
                {(formData.unit || "").toUpperCase().includes("LB") ? (
                  <div className="flex items-center gap-2 mt-1">
                    <Input value="16" disabled className="bg-muted/50" />
                    <span className="text-sm text-muted-foreground">oz per LB</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mt-1">
                    <Input 
                      data-testid="product-oz-conversion-input"
                      type="number"
                      value={formData.oz_conversion}
                      onChange={(e) => setFormData(p => ({ ...p, oz_conversion: e.target.value }))}
                      placeholder="Enter oz per unit"
                      min="0"
                      step="0.1"
                    />
                    <span className="text-sm text-muted-foreground">oz per unit</span>
                  </div>
                )}
              </div>
            )}
            
            {/* Pans to Oz Conversion */}
            {formData.allow_in_waste_log && (
              <div className="pt-2">
                <Label>
                  Pans to Oz Conversion
                  <span className="text-xs text-muted-foreground ml-2">(Optional - for pan-based waste tracking)</span>
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input 
                    data-testid="product-pans-to-oz-input"
                    type="number"
                    value={formData.pans_to_oz}
                    onChange={(e) => setFormData(p => ({ ...p, pans_to_oz: e.target.value }))}
                    placeholder="e.g., 128"
                    min="0"
                    step="0.1"
                  />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">oz per pan</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Allows waste to be logged in both pans AND oz, combined automatically
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} className="bg-primary" data-testid="save-product-btn">
              <Check size={16} className="mr-2" />
              {editingProduct ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Migration Dialog */}
      <Dialog open={migrationDialogOpen} onOpenChange={setMigrationDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-wide">
              PRODUCT MIGRATION
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-muted-foreground">
              This will consolidate duplicate products across locations into single entries with multiple location mappings.
            </p>
            
            {migrationStatus && (
              <div className="grid grid-cols-3 gap-4">
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground">Total Products</p>
                  <p className="text-2xl font-semibold">{migrationStatus.total_products}</p>
                </Card>
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground">Migrated</p>
                  <p className="text-2xl font-semibold text-green-600">{migrationStatus.migrated}</p>
                </Card>
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-2xl font-semibold text-orange-600">{migrationStatus.not_migrated}</p>
                </Card>
              </div>
            )}

            <Tabs defaultValue="auto" className="w-full">
              <TabsList>
                <TabsTrigger value="auto">Auto-Merge ({duplicates.duplicates?.length || 0})</TabsTrigger>
                <TabsTrigger value="conflicts">Conflicts ({duplicates.conflicts?.length || 0})</TabsTrigger>
              </TabsList>
              
              <TabsContent value="auto" className="mt-4">
                {duplicates.duplicates?.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground mb-4">
                      These products have the same name and can be automatically merged:
                    </p>
                    {duplicates.duplicates.slice(0, 10).map((group, idx) => (
                      <div key={group.name || idx} className="p-3 bg-muted/30 rounded-lg">
                        <p className="font-medium">{group.name}</p>
                        <div className="flex gap-2 mt-2">
                          {group.products.map(p => (
                            <Badge key={p.product_id} variant="secondary">
                              {p.location_name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                    {duplicates.duplicates.length > 10 && (
                      <p className="text-sm text-muted-foreground">
                        And {duplicates.duplicates.length - 10} more...
                      </p>
                    )}
                    <Button onClick={handleAutoMerge} className="mt-4">
                      <ArrowsClockwise size={16} className="mr-2" />
                      Auto-Merge All ({duplicates.duplicates.length} groups)
                    </Button>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    No products to auto-merge.
                  </p>
                )}
              </TabsContent>
              
              <TabsContent value="conflicts" className="mt-4">
                {duplicates.conflicts?.length > 0 ? (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      These products have conflicts that need manual resolution:
                    </p>
                    {duplicates.conflicts.map((group, idx) => (
                      <Card key={group.name || idx} className="p-4 border-orange-200">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{group.name}</p>
                            <p className="text-sm text-orange-600">{group.conflict_reason?.join(", ")}</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setConflictResolution(group)}
                          >
                            Resolve
                          </Button>
                        </div>
                        <div className="mt-3 space-y-2">
                          {group.products.map(p => (
                            <div key={p.product_id} className="flex justify-between text-sm bg-muted/30 p-2 rounded">
                              <span>{p.location_name}</span>
                              <span>Unit: {p.unit} | Price: ${p.price?.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    No conflicts to resolve.
                  </p>
                )}
              </TabsContent>
            </Tabs>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMigrationDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conflict Resolution Dialog */}
      <Dialog open={!!conflictResolution} onOpenChange={() => setConflictResolution(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-wide">
              RESOLVE CONFLICT: {conflictResolution?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-muted-foreground">
              Select the product with the correct price and unit to keep. Other products will be merged into it.
            </p>
            <div className="space-y-2">
              {conflictResolution?.products.map(p => (
                <div 
                  key={p.product_id} 
                  className="p-4 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => {
                    const sources = conflictResolution.products
                      .filter(x => x.product_id !== p.product_id)
                      .map(x => x.product_id);
                    handleResolveConflict(p.product_id, sources);
                  }}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{p.location_name}</p>
                      <p className="text-sm text-muted-foreground">
                        Unit: {p.unit} | Price: ${p.price?.toFixed(2)}
                      </p>
                    </div>
                    <Badge variant="outline">Select</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConflictResolution(null)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Component for assigning location to orphaned products
function OrphanedProductRow({ product, locations, allStorageAreas, onAssign }) {
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedArea, setSelectedArea] = useState("");
  const [saving, setSaving] = useState(false);

  const areasForLocation = allStorageAreas.filter(a => a.location_id === selectedLocation);

  const handleAssign = async () => {
    if (!selectedLocation || !selectedArea) {
      return;
    }
    setSaving(true);
    await onAssign(product.product_id, selectedLocation, selectedArea);
    setSaving(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 p-2 bg-white rounded-lg border border-orange-200">
      <div className="flex-1 min-w-[150px]">
        <span className="font-medium text-sm">{product.name}</span>
        <span className="text-xs text-muted-foreground ml-2">{product.unit}</span>
        {product.price > 0 && (
          <span className="text-xs text-green-600 ml-2">${product.price}</span>
        )}
      </div>
      <Select value={selectedLocation} onValueChange={(val) => { setSelectedLocation(val); setSelectedArea(""); }}>
        <SelectTrigger className="w-36 h-8 text-xs">
          <SelectValue placeholder="Location" />
        </SelectTrigger>
        <SelectContent>
          {locations.map(loc => (
            <SelectItem key={loc.location_id} value={loc.location_id}>
              {loc.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={selectedArea} onValueChange={setSelectedArea} disabled={!selectedLocation}>
        <SelectTrigger className="w-36 h-8 text-xs">
          <SelectValue placeholder="Storage Area" />
        </SelectTrigger>
        <SelectContent>
          {areasForLocation.map(area => (
            <SelectItem key={area.area_id} value={area.area_id}>
              {area.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button 
        size="sm" 
        className="h-8"
        onClick={handleAssign} 
        disabled={!selectedLocation || !selectedArea || saving}
      >
        {saving ? "..." : "Assign"}
      </Button>
    </div>
  );
}
